import {Router} from '@angular/router';

import {EventEmitter} from '@angular/core';
import {Observable, Subject, Subscription} from 'rxjs';

import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';

import {HttpStatusCodesEnum} from '@spa/api';
import {ApiError, ApiResponse, ReportFilter} from '@spa/api/lib/common';

import {Pagination, ReportTableData, ReportTableDataRow, } from 'appShared/components';
import {EventsService} from 'appEntries/panel/services';
import {CacheCollectionService} from 'appShared/services';
import {BaseReportComponent} from './base-report-component';

type filterDataType = number | string | boolean;

export abstract class SesAndMessagingReportComponent<ReportInterfaceType, ReportModelType>
    extends BaseReportComponent<ReportInterfaceType, ReportModelType> {
    public filterModelSubject: Subject<ReportFilter>;
    public filterModel: ReportFilter;

    protected constructor(
        eventsService: EventsService,
        router: Router,
        cacheCollectionService: CacheCollectionService
    ) {
        super(eventsService, router, cacheCollectionService);
    }

    private handleResponse(response: ApiResponse<ApiError[] | ReportInterfaceType[]>) {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            this.router
            .navigate(['/error'], {
                state: {
                    statuses: [response.status],
                    messages: response.messages
                }
            })
            .finally();

            return;
        }

        const paginateTo = Pagination.calculateItemsPerPage(this.filterModel.reportSize);

        this.filterResultSuccessHandler(this.paginationConfig.currentPage, 0, paginateTo, response);
    }

    abstract getInitialFilterModel(itemsPerPage?: number): ReportFilter;

    abstract getFilterModel(data: { [p: string]: filterDataType }): ReportFilter;

    abstract getReportHandler(filter: ReportFilter): Observable<ApiResponse<ApiError[] | ReportInterfaceType[]>>;

    onInit(): void {
        super.onInit();

        this.paginationConfig = {
            itemsPerPage: Pagination.defaultItemsPerPage,
            totalItems: this.modelsCollection.length,
            pagesChunk: Pagination.defaultPagesChunk,
            configChanged: new EventEmitter(),
            currentPage: Pagination.defaultPage
        };

        this.filterModelSubject = new Subject();

        this.filterSubjectSubscription = this.filterModelSubject.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            switchMap((filter: ReportFilter) => this.getReportHandler(filter)),
        ).subscribe((response: ApiResponse<ApiError[] | ReportInterfaceType[]>) => this.handleResponse(response));
    }

    forceFilterData(filter: ReportFilter): Subscription {
        return this.getReportHandler(filter)
        .subscribe((response: ApiResponse<ApiError[] | ReportInterfaceType[]>) => this.handleResponse(response));
    }

    onFilterChanged(data: { [key: string]: string | number | boolean }): void {
        this.paginationConfig.currentPage = Pagination.initCurrentPage();

        this.filterModel = this.getFilterModel(data);

        this.filterModelSubject.next(this.filterModel);
    }

    mapDataSourceResponse(row: ReportTableDataRow): ReportTableDataRow {
        row.dataItems.map((item: ReportTableData) => {
            if (this.dataTableConfig.hiddenColumnIds.indexOf(item.colId) >= 0) {
                item.hidden = true;
            }
        });

        return row;
    }

    fixPaginationAndDataSource(initialPaginationPage: number, paginateFrom: number, paginateTo: number): void {
        this.paginationConfig.pagesChunk = this.filterModel.pageNumber;
        this.paginationConfig.totalItems = this.modelsCollection.length;

        this.paginationConfig.itemsPerPage =
            Pagination.calculateItemsPerPage(this.filterModel.reportSize);

        this.paginationConfig.configChanged.emit({initialPage: initialPaginationPage});

        const paginatedCollection = this.paginateCollection<ReportModelType>(
            this.modelsCollection,
            paginateFrom,
            paginateTo
        );

        this.dataTableConfig.rows = this.createDataSource(paginatedCollection)
        .map((row: ReportTableDataRow) => this.mapDataSourceResponse(row));
    }
}
